import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listRestaurants, setRestaurantStatus, type Restaurant } from '@/shared/services/restaurantsService';
import { ImageCarousel } from '@/shared/components/ImageCarousel';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import styles from './RestaurantsPage.module.css';

const STATUS_LABELS: Record<Restaurant['status'], string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  suspendido: 'Suspendido',
};

const OPERATIONAL_STATUS_LABELS: Record<Restaurant['operationalStatus'], string> = {
  abierto: 'Abierto',
  cerrado: 'Cerrado',
};

export function RestaurantsPage() {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    restaurantId: string | null;
    restaurantName: string;
    currentStatus: Restaurant['status'];
    newStatus: Restaurant['status'] | null;
  }>({
    isOpen: false,
    restaurantId: null,
    restaurantName: '',
    currentStatus: 'activo',
    newStatus: null,
  });

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const restaurantsList = await listRestaurants();
      setRestaurants(restaurantsList);
    } catch (err) {
      let errorMessage = 'Error al cargar comercios';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string }; status?: number } };
        if (axiosError.response?.status === 404) {
          errorMessage = 'El endpoint no está disponible. Verifica que el backend esté desplegado.';
        } else if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          errorMessage = 'No tienes permisos para ver comercios.';
        } else {
          errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || errorMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp?: { seconds: number; nanoseconds: number }) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenModal = (restaurant: Restaurant, newStatus: Restaurant['status']) => {
    setModalState({
      isOpen: true,
      restaurantId: restaurant.restaurantId,
      restaurantName: restaurant.name,
      currentStatus: restaurant.status,
      newStatus,
    });
  };

  const handleCloseModal = () => {
    if (processingId) return; // No cerrar si está procesando
    setModalState({
      isOpen: false,
      restaurantId: null,
      restaurantName: '',
      currentStatus: 'activo',
      newStatus: null,
    });
  };

  const handleConfirmStatus = async (_inputValue?: string) => {
    if (!modalState.restaurantId || !modalState.newStatus) return;

    try {
      setProcessingId(modalState.restaurantId);
      setError('');
      setSuccessMessage('');

      await setRestaurantStatus(modalState.restaurantId, modalState.newStatus);

      // Actualizar el estado local
      setRestaurants((prev) =>
        prev.map((r) =>
          r.restaurantId === modalState.restaurantId
            ? { ...r, status: modalState.newStatus! }
            : r
        )
      );

      const statusLabel = STATUS_LABELS[modalState.newStatus];
      setSuccessMessage(`Comercio ${statusLabel.toLowerCase()} exitosamente`);

      // Cerrar modal
      handleCloseModal();
    } catch (err) {
      let errorMessage = 'Error al cambiar el estado del comercio';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string }; status?: number } };
        if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          errorMessage = 'No tienes permisos para cambiar el estado del comercio.';
        } else {
          errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || errorMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setProcessingId(null);
    }
  };

  const getModalContent = () => {
    if (!modalState.newStatus) return { title: '', message: '', variant: 'primary' as const };

    const restaurantName = modalState.restaurantName;


    if (modalState.newStatus === 'inactivo') {
      return {
        title: '¿Desactivar comercio?',
        message: `¿Estás seguro de que deseas desactivar el comercio "${restaurantName}"?\n\nEl comercio dejará de aparecer para los clientes y no podrá recibir nuevos pedidos hasta que sea reactivado.`,
        variant: 'danger' as const,
        confirmText: 'Desactivar',
      };
    }

    if (modalState.newStatus === 'suspendido') {
      return {
        title: '¿Suspender comercio?',
        message: `¿Estás seguro de que deseas suspender el comercio "${restaurantName}"?\n\nEl comercio dejará de aparecer para los clientes y no podrá recibir nuevos pedidos. Esta acción generalmente se realiza por violación de políticas o problemas de calidad.`,
        variant: 'warning' as const,
        confirmText: 'Suspender',
      };
    }

    if (modalState.newStatus === 'activo') {
      const action = modalState.currentStatus === 'suspendido' ? 'reactivar' : 'activar';
      return {
        title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} comercio?`,
        message: `¿Estás seguro de que deseas ${action} el comercio "${restaurantName}"?\n\nEl comercio volverá a aparecer para los clientes y podrá recibir pedidos nuevamente.`,
        variant: 'primary' as const,
        confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      };
    }

    return { title: '', message: '', variant: 'primary' as const };
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando comercios...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Comercios Aprobados</h1>
        <p className={styles.subtitle}>Gestiona todos los comercios aprobados del sistema</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {successMessage && <div className={styles.success}>{successMessage}</div>}

      {restaurants.length === 0 ? (
        <div className={styles.empty}>
          <p>No hay comercios aprobados en este momento.</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Dirección</th>
                <th>Estado</th>
                <th>Estado Operativo</th>
                <th>Imágenes</th>
                <th>Fecha Creación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {restaurants.map((restaurant) => (
                <tr key={restaurant.restaurantId}>
                  <td>
                    <strong>{restaurant.name}</strong>
                  </td>
                  <td>{restaurant.email}</td>
                  <td>{restaurant.phone}</td>
                  <td>{restaurant.address.text}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[`status${restaurant.status}`]}`}>
                      {STATUS_LABELS[restaurant.status]}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.operationalBadge} ${styles[`operational${restaurant.operationalStatus}`]}`}>
                      {OPERATIONAL_STATUS_LABELS[restaurant.operationalStatus]}
                    </span>
                  </td>
                  <td>
                    <ImageCarousel images={restaurant.images || []} maxImages={3} />
                  </td>
                  <td>{formatDate(restaurant.createdAt)}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        onClick={() => navigate(`/restaurants/${restaurant.restaurantId}`)}
                        className={`${styles.actionButton} ${styles.detailButton}`}
                        title="Ver detalles del restaurante"
                      >
                        Ver Detalle
                      </button>
                      {restaurant.status === 'activo' && (
                        <>
                          <button
                            onClick={() => handleOpenModal(restaurant, 'inactivo')}
                            disabled={processingId === restaurant.restaurantId}
                            className={`${styles.actionButton} ${styles.deactivateButton}`}
                            title="Desactivar restaurante"
                          >
                            Desactivar
                          </button>
                          <button
                            onClick={() => handleOpenModal(restaurant, 'suspendido')}
                            disabled={processingId === restaurant.restaurantId}
                            className={`${styles.actionButton} ${styles.suspendButton}`}
                            title="Suspender restaurante"
                          >
                            Suspender
                          </button>
                        </>
                      )}
                      {restaurant.status === 'inactivo' && (
                        <button
                          onClick={() => handleOpenModal(restaurant, 'activo')}
                          disabled={processingId === restaurant.restaurantId}
                          className={`${styles.actionButton} ${styles.activateButton}`}
                          title="Activar restaurante"
                        >
                          Activar
                        </button>
                      )}
                      {restaurant.status === 'suspendido' && (
                        <button
                          onClick={() => handleOpenModal(restaurant, 'activo')}
                          disabled={processingId === restaurant.restaurantId}
                          className={`${styles.actionButton} ${styles.activateButton}`}
                          title="Reactivar restaurante"
                        >
                          Reactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.count}>Total: {restaurants.length} comercios aprobados</p>
        <button onClick={loadRestaurants} className={styles.refreshButton} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar Lista'}
        </button>
      </div>

      {/* Modal de confirmación */}
      {modalState.isOpen && (
        <ConfirmModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmStatus}
          title={getModalContent().title}
          message={getModalContent().message}
          confirmText={getModalContent().confirmText}
          cancelText="Cancelar"
          confirmButtonVariant={getModalContent().variant}
          isLoading={processingId === modalState.restaurantId}
        />
      )}
    </div>
  );
}
